// Owned by mju-ai-workbench/shared/schemas/nodeShell.ts — the node console's contract (D368). The AIOE session mirrors it to mju-aioe/packages/shared/src/schemas/nodeShell.ts; the workbench never mirrors it back. Keep the two identical.
/**
 * The node console (D368): a shell on a node, in the browser of the organisation's console and on the phone, with no
 * SSH key and no open port — the relay's WebSocket leg (D343, `relayChannel.ts`) carries it like a terminal.
 *
 *   node.shell.subscribe   CHANNEL method, served by a NODE only. `input: true` when the person types.
 *                          [shell:read to open and watch, + shell:write for input]
 *                          open params {@link NodeShellParams}; `opened.result` {@link NodeShellOpened}; `data` = base64
 *                          of the raw PTY bytes, at most {@link NODE_SHELL_DATA_CHUNK_BYTES} raw a frame; `input`
 *                          payloads {@link NodeShellInput} (`keys` and `resize`, as `terminals.subscribe`); channel
 *                          `close` ends the shell. A desktop refuses every open with `shell_unavailable`.
 *
 * Each open starts a fresh shell (`$SHELL -l`, else `/bin/bash -l`, else `/bin/sh -l`; PowerShell on Windows) as the
 * node's service account, in its home folder or `cwd`, which must be inside the node's home or a repository it serves.
 * There is no scrollback: the shell is new. Its environment is the service account's without the node's own tokens.
 *
 * Consent is the node's: `remote.shell.enabled` (default off) and `remote.shell.allowRoot` (default off: a node whose
 * service account is root, or SYSTEM on Windows, refuses), both lockable by the organisation's policy. The node's kill
 * switch ends every live shell with `revoked`; so does switching `remote.shell.enabled` off.
 *
 * Refusals (a channel `closed { code, reason }` whose reason starts `<error>: ` and goes on in a sentence for a person):
 *
 *   shell_off           closed `refused`  the node's switch is off (`mojoup-node config set remote.shell.enabled true`)
 *   shell_unavailable   closed `refused`  a desktop, a Windows node with no pseudo-terminal, root without allowRoot
 *   scope               closed `scope`    the device was not given shell:read (or shell:write for an `input: true` open)
 *   limit               closed `limit`    {@link NODE_SHELL_MAX_OPEN} shells are open on the node already
 *   outside_home        closed `refused`  `cwd` is not inside the node's home or a repository it serves
 *
 * A live shell ends `done` when it exits, `idle` after {@link NODE_SHELL_IDLE_MS} with no input, `revoked` on the kill
 * switch or the switch going off, and `workbench_closed` when the node's owner ends it (`mojoup-node shells stop`).
 *
 * Audit, on the node (never the content): {@link NODE_SHELL_AUDIT}`.open` and `.close` with the person (the actor's
 * principal), the channel, and on close the seconds, bytes in and out and how it ended. The rows are always written.
 * `remote.shell.recording`: `metadata` (the default) and `off` write those rows and nothing else (`off` is there so a
 * policy can say so plainly); `full` also writes the shell's output (and resizes, never the keys typed) as an
 * encrypted asciicast v2 file in the node's data folder, kept {@link NODE_SHELL_RECORDING_RETENTION_DAYS} days. A
 * recording never leaves the node.
 *
 * zod + types + constants only; no workbench import beyond the companion's terminal shapes, so a mirror is a copy.
 */
import { z } from "zod";
import { TERMINAL_DATA_CHUNK_BYTES, TERMINAL_MAX_COLS, TERMINAL_MAX_ROWS, terminalInputSchema } from "./companion.js";
export const NODE_SHELL_METHOD = "node.shell.subscribe";
/** Open and watch a node shell. Opt-in: never in any default preset (the phone's `COMPANION_SCOPES` has neither). */
export const NODE_SHELL_READ_SCOPE = "shell:read";
/** Type into it and resize it. AIOE asks a step-up for it. Never implied by `shell:read`. */
export const NODE_SHELL_WRITE_SCOPE = "shell:write";
export const NODE_SHELL_SCOPES = [NODE_SHELL_READ_SCOPE, NODE_SHELL_WRITE_SCOPE];
/** Most raw PTY bytes in one `data` frame, as a terminal's (base64 keeps it inside the channel's 64 KB frame). */
export const NODE_SHELL_DATA_CHUNK_BYTES = TERMINAL_DATA_CHUNK_BYTES;
export const NODE_SHELL_MIN_COLS = 2;
export const NODE_SHELL_MIN_ROWS = 2;
export const NODE_SHELL_MAX_COLS = TERMINAL_MAX_COLS;
export const NODE_SHELL_MAX_ROWS = TERMINAL_MAX_ROWS;
/** Most raw bytes one `keys` input may carry; a larger one is dropped. */
export const NODE_SHELL_KEYS_MAX_BYTES = 16 * 1024;
/** Shells open on one node at once, across every person. */
export const NODE_SHELL_MAX_OPEN = 2;
/** A shell with no input for this long ends `idle`. */
export const NODE_SHELL_IDLE_MS = 30 * 60_000;
/** How long a `full` recording is kept on the node. */
export const NODE_SHELL_RECORDING_RETENTION_DAYS = 30;
/** The node's settings for it (global, on the node; the organisation may lock each). */
export const NODE_SHELL_SETTINGS = {
    enabled: "remote.shell.enabled",
    allowRoot: "remote.shell.allowRoot",
    recording: "remote.shell.recording",
};
export const NODE_SHELL_RECORDING_MODES = ["off", "metadata", "full"];
export const nodeShellRecordingSchema = z.enum(NODE_SHELL_RECORDING_MODES);
/** The head of a refusal's `reason` (`"<error>: <sentence>"`). */
export const NODE_SHELL_ERRORS = {
    off: "shell_off",
    unavailable: "shell_unavailable",
    scope: "scope",
    limit: "limit",
    outsideHome: "outside_home",
};
/** The audit actions the node writes. */
export const NODE_SHELL_AUDIT = {
    open: "remote.shell.open",
    close: "remote.shell.close",
};
/**
 * `node.shell.subscribe`'s open params. `cols` and `rows` are clamped to {@link NODE_SHELL_MIN_COLS}–{@link NODE_SHELL_MAX_COLS}
 * and {@link NODE_SHELL_MIN_ROWS}–{@link NODE_SHELL_MAX_ROWS} (see {@link normaliseNodeShellParams}); `cwd` is an absolute
 * path on the node, inside its home or a repository it serves (else `outside_home`); left out, the node's home.
 */
export const nodeShellParamsSchema = z.object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
    cwd: z.string().min(1).max(4096).optional(),
});
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, Math.round(n)));
/** The size a shell opens at, as the node clamps it. */
export function normaliseNodeShellParams(p) {
    return { cols: clamp(p.cols, NODE_SHELL_MIN_COLS, NODE_SHELL_MAX_COLS), rows: clamp(p.rows, NODE_SHELL_MIN_ROWS, NODE_SHELL_MAX_ROWS), ...(p.cwd !== undefined ? { cwd: p.cwd } : {}) };
}
/** `opened.result`: the size it opened at, the shell, the folder it started in and the account it runs as. No scrollback. */
export const nodeShellOpenedSchema = z.object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
    /** The shell's program, as the node ran it (`/bin/bash`, `pwsh.exe`). */
    shell: z.string(),
    cwd: z.string(),
    /** The node's service account (the shell runs as it). */
    user: z.string(),
});
/** An `input` frame's payload: the terminal's own (`keys` = base64 of at most 16 KB raw; `resize`). */
export const nodeShellInputSchema = terminalInputSchema;
/** `detail` of a {@link NODE_SHELL_AUDIT}`.close` row. */
export const nodeShellCloseDetailSchema = z.object({
    channel: z.string(),
    code: z.string(),
    seconds: z.number().int().nonnegative(),
    bytesIn: z.number().int().nonnegative(),
    bytesOut: z.number().int().nonnegative(),
    recording: nodeShellRecordingSchema,
});
//# sourceMappingURL=nodeShell.js.map